import { defineStore } from 'pinia'
import { ref } from 'vue'
import { io, Socket } from 'socket.io-client'
import type { DeviceMetricEvent, DeviceStatusEvent } from '@/types/jispman'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

export const useNotificationStore = defineStore('notifications', () => {
  const toasts = ref<Toast[]>([])
  const socket = ref<Socket | null>(null)
  const connected = ref(false)

  let _toastCounter = 0

  function addToast(t: Omit<Toast, 'id'>): string {
    const id = String(++_toastCounter)
    const duration = t.duration ?? 4000
    toasts.value.push({ ...t, id, duration })
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration)
    }
    return id
  }

  function removeToast(id: string) {
    const idx = toasts.value.findIndex(t => t.id === id)
    if (idx !== -1) toasts.value.splice(idx, 1)
  }

  function success(title: string, message?: string) {
    return addToast({ type: 'success', title, message })
  }

  function error(title: string, message?: string) {
    return addToast({ type: 'error', title, message, duration: 6000 })
  }

  function warning(title: string, message?: string) {
    return addToast({ type: 'warning', title, message })
  }

  function info(title: string, message?: string) {
    return addToast({ type: 'info', title, message })
  }

  // Device metric/status callbacks — registered by the NOC views
  type MetricHandler = (e: DeviceMetricEvent) => void
  type StatusHandler = (e: DeviceStatusEvent) => void
  const _metricHandlers: MetricHandler[] = []
  const _statusHandlers: StatusHandler[] = []

  function onDeviceMetric(fn: MetricHandler) { _metricHandlers.push(fn) }
  function onDeviceStatus(fn: StatusHandler) { _statusHandlers.push(fn) }
  function offDeviceMetric(fn: MetricHandler) {
    const i = _metricHandlers.indexOf(fn); if (i !== -1) _metricHandlers.splice(i, 1)
  }
  function offDeviceStatus(fn: StatusHandler) {
    const i = _statusHandlers.indexOf(fn); if (i !== -1) _statusHandlers.splice(i, 1)
  }

  function connect(accessToken: string, siteId: string) {
    if (socket.value?.connected) return

    const s = io('/', {
      auth: { token: accessToken },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    })

    s.on('connect', () => {
      connected.value = true
      s.emit('join:site', siteId)
    })

    s.on('disconnect', () => { connected.value = false })

    s.on('device:metric', (e: DeviceMetricEvent) => {
      _metricHandlers.forEach(fn => fn(e))
    })

    s.on('device:status', (e: DeviceStatusEvent) => {
      _statusHandlers.forEach(fn => fn(e))
    })

    socket.value = s
  }

  function disconnect() {
    socket.value?.disconnect()
    socket.value = null
    connected.value = false
  }

  return {
    toasts,
    connected,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    onDeviceMetric,
    onDeviceStatus,
    offDeviceMetric,
    offDeviceStatus,
    connect,
    disconnect,
  }
})
